import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { authenticatedMiddleware } from '../../permissions/middlewares/authenticatedMiddleware.js';
import { AppliedContract } from '../entities/AppliedContract.js';
import { Contract } from '../entities/Contract.js';
import { ContractFile } from '../entities/ContractFile.js';
import { ContractMetaInformation } from '../entities/ContractMetaInformation.js';
import { ContractSupplement } from '../entities/ContractSupplement.js';
import { ContractSupplementFile } from '../entities/ContractSupplementFile.js';
import { resolveCalculateTaetigkeitenschluessel } from '../server/resolvers/resolveCalculateTaetigkeitenschluessel.js';
import { resolveGenerateDocumentFromContract } from '../server/resolvers/resolveGenerateDocumentFromContract.js';
import { resolveGenerateDocumentFromSupplement } from '../server/resolvers/resolveGenerateDocumentFromSupplement.js';
import { resolveGetAppliedContract } from '../server/resolvers/resolveGetAppliedContract.js';
import { resolveSubscribeContractByUserID } from '../server/resolvers/resolveGetContractByUserID.js';
import { resolveNotifyChangeInMavProcess } from '../server/resolvers/resolveNotifyChangeInMavProcess.js';
import { resolveNotifyMAVForChange } from '../server/resolvers/resolveNotifyMAVForChange.js';
import { resolveSaveSupplementToBaseContract } from '../server/resolvers/resolveSaveSupplementToBaseContract.js';
import { resolveSubscribeAllContractDocumentsForContract } from '../server/resolvers/resolveSubscribeAllContractDocumentsForContract.js';
import { resolveSubscribeAllContractSupplementDocumentsForContract } from '../server/resolvers/resolveSubscribeAllContractSupplementDocumentsForContract.js';
import {
  resolveSubscribeMetaInformationForContract,
  resolveSubscribeMetaInformationForContracts,
} from '../server/resolvers/resolveSubscribeMetaInformationForContract.js';

@Entity()
export class ContractApplyOptions extends AdornisEntity {
  static override _class = 'ContractApplyOptions';

  constructor(doc: PartialEntityData<ContractApplyOptions>) {
    super(doc);
  }

  @Field(() => Boolean, { default: v => v ?? false })
  public includeDrafts: Maybe<boolean>;

  @Field(() => Boolean, { default: v => v ?? false })
  public overrideActiveCheck: Maybe<boolean>;

  @Field(() => Date, { default: v => v ?? new Date() })
  public applyDate: Maybe<Date>;

  @Field(() => Boolean, { default: v => v ?? false })
  public onlyRecentHistory: Maybe<boolean>;

  @Field(() => Int)
  public subIndexSelected: Maybe<number>;
}

const subscribeContractByUserID = registerSubscription({
  operationName: 'subscribeContractByUserID',
  type: () => [Contract],
  params: [{ type: () => ID, name: 'userID' }],
  middlewares: [authenticatedMiddleware],
  resolve: resolveSubscribeContractByUserID,
});

const getAppliedContract = registerMutation({
  operationName: 'getAppliedContract',
  type: () => AppliedContract,
  params: [
    { type: () => ID, name: 'contractID' },
    { type: () => ContractApplyOptions, name: 'options' },
  ],
  middlewares: [authenticatedMiddleware],
  resolve: resolveGetAppliedContract,
});

const saveSupplementToBaseContract = registerMutation({
  operationName: 'saveSupplementToBaseContract',
  type: () => Boolean,
  params: [
    { type: () => ID, name: 'contractID' },
    { type: () => ContractSupplement, name: 'supplement' },
  ],
  middlewares: [authenticatedMiddleware],
  resolve: resolveSaveSupplementToBaseContract,
});

const subscribeAllContractDocumentsForContract = registerSubscription({
  type: () => [ContractFile],
  operationName: 'subscribeAllContractDocumentsForContract',
  params: [{ type: () => ID, name: 'contractID' }],
  middlewares: [authenticatedMiddleware],
  resolve: resolveSubscribeAllContractDocumentsForContract,
});

const subscribeAllContractSupplementDocumentsForContract = registerSubscription({
  type: () => [ContractSupplementFile],
  operationName: 'subscribeAllContractSupplementDocumentsForContract',
  params: [{ type: () => ID, name: 'supplementID' }],
  middlewares: [authenticatedMiddleware],
  resolve: resolveSubscribeAllContractSupplementDocumentsForContract,
});

const generateDocumentFromContract = registerMutation({
  operationName: 'generateDocumentFromContract',
  type: () => ContractFile,
  params: [
    { type: () => ID, name: 'contractID' },
    { type: () => Date, name: 'applyDate' },
    { type: () => Boolean, name: 'onlyInfo' },
  ],
  middlewares: [authenticatedMiddleware],
  resolve: resolveGenerateDocumentFromContract,
});

const generateDocumentFromSupplement = registerMutation({
  operationName: 'generateDocumentFromSupplement',
  type: () => ContractSupplementFile,
  params: [
    { type: () => ID, name: 'contractID' },
    { type: () => Date, name: 'applyDate' },
    { type: () => ID, name: 'supplementID' },
  ],
  middlewares: [authenticatedMiddleware],
  resolve: resolveGenerateDocumentFromSupplement,
});

const subscribeMetaInformationForContract = registerSubscription({
  operationName: 'subscribeMetaInformationForContract',
  type: () => ContractMetaInformation,
  params: [{ type: () => ID, name: 'contractID' }],
  middlewares: [authenticatedMiddleware],
  resolve: resolveSubscribeMetaInformationForContract,
});

const subscribeMetaInformationForContracts = registerSubscription({
  operationName: 'subscribeMetaInformationForContracts',
  type: () => [ContractMetaInformation],
  params: [{ type: () => [ID], name: 'contractIDs' }],
  middlewares: [authenticatedMiddleware],
  resolve: resolveSubscribeMetaInformationForContracts,
});

const calculateTaetigkeitenschluessel = registerMutation({
  operationName: 'calculateTaetigkeitenschluessel',
  type: () => [String],
  params: [{ type: () => AppliedContract, name: 'contract' }],
  middlewares: [authenticatedMiddleware],
  resolve: resolveCalculateTaetigkeitenschluessel,
});

const notifyMAVForChange = registerMutation({
  operationName: 'notifyMAVForChange',
  type: () => Boolean,
  params: [
    { type: () => ID, name: 'contractID' },
    { type: () => String, name: 'comment' },
    { type: () => ID, name: 'supplementID' },
    { type: () => Boolean, name: 'changed' },
  ],
  middlewares: [authenticatedMiddleware],
  resolve: resolveNotifyMAVForChange,
});

const notifyChangeInMavProcess = registerMutation({
  operationName: 'notifyChangeInMavProcess',
  type: () => Boolean,
  params: [
    { type: () => ID, name: 'contractID' },
    { type: () => String, name: 'role' },
    { type: () => String, name: 'text' },
    { type: () => ID, name: 'supplementID' },
  ],
  middlewares: [authenticatedMiddleware],
  resolve: resolveNotifyChangeInMavProcess,
});

export const ContractAPI = {
  subscribeByUserID: subscribeContractByUserID,
  getApplied: getAppliedContract,
  saveSupplementToBase: saveSupplementToBaseContract,
  subscribeAllContractDocuments: subscribeAllContractDocumentsForContract,
  subscribeAllContractSupplementDocuments: subscribeAllContractSupplementDocumentsForContract,
  generateContractDocx: generateDocumentFromContract,
  generateSupplementDocx: generateDocumentFromSupplement,
  subscribeMeta: subscribeMetaInformationForContract,
  subscribeMetaContracts: subscribeMetaInformationForContracts,
  notifyMAV: notifyMAVForChange,
  notifyChange: notifyChangeInMavProcess,
  calculateTaetigkeitenschluessel: calculateTaetigkeitenschluessel,
};
